import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Award, Leaf, Star } from "lucide-react";
import FadeIn from "../components/FadeIn";
import { CATEGORIES, TESTIMONIALS, FEATURED_QUOTE, CONTACT } from "../data/salon";

const HERO_IMAGE =
  "https://images.pexels.com/photos/14724697/pexels-photo-14724697.jpeg?auto=compress&cs=tinysrgb&w=1600";
const INTERIOR_IMAGE =
  "https://images.pexels.com/photos/3993449/pexels-photo-3993449.jpeg?auto=compress&cs=tinysrgb&w=1600";

export default function Home() {
  return (
    <main data-testid="home-page">
      {/* === HERO === */}
      <section className="relative w-full" style={{ paddingTop: "120px" }}>
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center min-h-[calc(100vh-120px)] py-12">
            {/* Text */}
            <div className="lg:col-span-5 order-2 lg:order-1">
              <FadeIn>
                <p className="eyebrow mb-8">
                  · Salon de lux · Cluj-Napoca
                </p>
              </FadeIn>

              <FadeIn delay={120}>
                <h1
                  data-testid="hero-headline"
                  className="font-serif-display text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[0.95]"
                  style={{ color: "#1A1A1A", fontWeight: 300 }}
                >
                  Frumusețea ta,
                  <br />
                  <span style={{ fontStyle: "italic", color: "#B8964E" }}>redefinită</span>.
                </h1>
              </FadeIn>

              <FadeIn delay={240}>
                <p
                  className="mt-8 text-lg leading-relaxed max-w-lg"
                  style={{ color: "#555", fontWeight: 300 }}
                >
                  Experiențe de lux în inima Clujului. Un studio dedicat femeilor
                  care prețuiesc detaliul, liniștea și rafinamentul.
                </p>
              </FadeIn>

              <FadeIn delay={360}>
                <div className="mt-12 flex flex-wrap items-center gap-6">
                  <Link to="/rezervari" data-testid="hero-book-cta" className="btn-lux">
                    Programează-te acum
                    <ArrowUpRight size={14} />
                  </Link>
                  <Link
                    to="/servicii"
                    data-testid="hero-services-link"
                    className="nav-link"
                  >
                    Descoperă serviciile
                  </Link>
                </div>
              </FadeIn>

              <FadeIn delay={480}>
                <div
                  className="mt-16 pt-8 grid grid-cols-3 gap-6"
                  style={{ borderTop: "1px solid #E8E0D5" }}
                >
                  <div>
                    <p className="font-serif-display text-3xl" style={{ color: "#B8964E" }}>
                      12+
                    </p>
                    <p className="eyebrow mt-2" style={{ color: "#999" }}>
                      Ani Experiență
                    </p>
                  </div>
                  <div>
                    <p className="font-serif-display text-3xl" style={{ color: "#B8964E" }}>
                      4
                    </p>
                    <p className="eyebrow mt-2" style={{ color: "#999" }}>
                      Categorii
                    </p>
                  </div>
                  <div>
                    <p className="font-serif-display text-3xl" style={{ color: "#B8964E" }}>
                      5★
                    </p>
                    <p className="eyebrow mt-2" style={{ color: "#999" }}>
                      Recenzii
                    </p>
                  </div>
                </div>
              </FadeIn>
            </div>

            {/* Image */}
            <div className="lg:col-span-7 order-1 lg:order-2 relative">
              <FadeIn delay={100}>
                <div className="relative">
                  <div
                    className="w-full overflow-hidden"
                    style={{
                      aspectRatio: "4/5",
                      maxHeight: "78vh",
                    }}
                  >
                    <img
                      src={HERO_IMAGE}
                      alt="Portret editorial de femeie elegantă"
                      className="w-full h-full object-cover"
                      style={{ filter: "saturate(0.92)" }}
                      data-testid="hero-image"
                    />
                  </div>
                  {/* Side caption */}
                  <div
                    className="hidden md:block absolute -left-4 top-8"
                    style={{
                      writingMode: "vertical-rl",
                      transform: "rotate(180deg)",
                    }}
                  >
                    <p className="tracking-wider-luxe text-xs" style={{ color: "#999" }}>
                      LUMIÈRE · EST. 2013 · CLUJ-NAPOCA
                    </p>
                  </div>
                  {/* Floating quote tag */}
                  <div
                    className="absolute -bottom-6 -left-6 md:-bottom-10 md:-left-10 bg-white px-6 py-5 max-w-[260px]"
                    style={{ border: "1px solid #E8E0D5" }}
                  >
                    <p className="eyebrow" style={{ color: "#B8964E" }}>Editorial Beauty</p>
                    <p className="font-serif-display text-xl mt-2 leading-snug" style={{ color: "#1A1A1A", fontStyle: "italic" }}>
                      „Un sanctuar pentru femeia modernă.”
                    </p>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* === BINE AI VENIT === */}
      <section className="py-28 md:py-40 marble-soft">
        <div className="mx-auto max-w-[820px] px-6 text-center">
          <FadeIn>
            <p className="eyebrow mb-8">— Bine ai venit —</p>
          </FadeIn>
          <FadeIn delay={120}>
            <h2
              className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-tight"
              style={{ color: "#1A1A1A", fontWeight: 300 }}
            >
              Un loc unde <span style={{ fontStyle: "italic", color: "#B8964E" }}>rafinamentul</span> se
              întâlnește cu îngrijirea personalizată.
            </h2>
          </FadeIn>
          <FadeIn delay={240}>
            <p className="mt-10 text-lg leading-loose" style={{ color: "#555", fontWeight: 300 }}>
              La Lumière Beauty Studio, fiecare detaliu este gândit pentru a-ți
              oferi un moment de pură evadare. Lucrăm exclusiv cu produse
              premium, alături de specialiști certificați internațional, într-o
              atmosferă liniștită, intimă și luminoasă — un spațiu unde
              frumusețea capătă forma ta personală.
            </p>
          </FadeIn>
          <FadeIn delay={360}>
            <div className="divider-gold mt-14">
              <Sparkles size={14} />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* === SERVICII === */}
      <section className="py-28 md:py-40">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <FadeIn>
              <div>
                <p className="eyebrow mb-4">— Universul nostru —</p>
                <h2 className="font-serif-display text-4xl sm:text-5xl" style={{ color: "#1A1A1A", fontWeight: 300 }}>
                  Serviciile <span style={{ fontStyle: "italic", color: "#B8964E" }}>noastre</span>
                </h2>
              </div>
            </FadeIn>
            <FadeIn delay={120}>
              <p className="max-w-sm text-sm leading-relaxed" style={{ color: "#555" }}>
                Patru lumi distincte, gândite pentru a-ți acoperi fiecare
                nevoie — de la păr și ten, până la unghii și sprâncene.
              </p>
            </FadeIn>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {CATEGORIES.map((c, i) => (
              <FadeIn key={c.id} delay={i * 90}>
                <Link
                  to={`/servicii#${c.id}`}
                  data-testid={`home-service-card-${c.id}`}
                  className="lux-card block group h-full"
                >
                  <div className="relative w-full overflow-hidden" style={{ aspectRatio: "4/5" }}>
                    <img
                      src={c.image}
                      alt={c.label}
                      className="w-full h-full object-cover transition-transform ease-out group-hover:scale-105"
                      style={{ transitionDuration: "1400ms" }}
                    />
                    <span
                      className="absolute top-4 left-4 font-serif-display text-xs tracking-wider-luxe"
                      style={{ color: "#fff", textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}
                    >
                      {c.tag}
                    </span>
                  </div>
                  <div className="p-7">
                    <h3 className="font-serif-display text-2xl" style={{ color: "#1A1A1A", fontWeight: 300 }}>
                      {c.label}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed" style={{ color: "#555" }}>
                      {c.description}
                    </p>
                    <span
                      className="inline-flex items-center gap-2 mt-6 text-xs tracking-wider-luxe"
                      style={{ color: "#B8964E" }}
                    >
                      VEZI MAI MULT <ArrowUpRight size={14} />
                    </span>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* === FULL-WIDTH QUOTE BANNER === */}
      <section
        className="relative py-32 md:py-44 marble-veined"
        style={{ borderTop: "1px solid #E8E0D5", borderBottom: "1px solid #E8E0D5" }}
      >
        <div className="mx-auto max-w-[1000px] px-6 text-center">
          <FadeIn>
            <p
              className="font-serif-display italic text-2xl sm:text-3xl md:text-[2.4rem] lg:text-[2.8rem] leading-[1.4]"
              style={{ color: "#1A1A1A", fontWeight: 300 }}
              data-testid="featured-quote"
            >
              „{FEATURED_QUOTE.text}”
            </p>
          </FadeIn>
          <FadeIn delay={200}>
            <div className="divider-gold mt-12">
              <Star size={12} fill="#B8964E" stroke="#B8964E" />
            </div>
            <p className="eyebrow mt-6">— {FEATURED_QUOTE.author} · {FEATURED_QUOTE.role}</p>
          </FadeIn>
        </div>
      </section>

      {/* === TESTIMONIALS === */}
      <section className="py-28 md:py-40">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <FadeIn>
            <div className="text-center mb-16">
              <p className="eyebrow mb-4">— Ce spun clientele —</p>
              <h2 className="font-serif-display text-4xl sm:text-5xl" style={{ color: "#1A1A1A", fontWeight: 300 }}>
                Recenzii <span style={{ fontStyle: "italic", color: "#B8964E" }}>autentice</span>
              </h2>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {TESTIMONIALS.map((t, i) => (
              <FadeIn key={t.name} delay={i * 120}>
                <div
                  className="lux-card p-9 h-full flex flex-col"
                  data-testid={`testimonial-${i}`}
                >
                  <div className="flex items-center gap-1">
                    {Array.from({ length: t.stars }).map((_, k) => (
                      <Star key={k} size={14} fill="#B8964E" stroke="#B8964E" />
                    ))}
                  </div>
                  <p
                    className="font-serif-display italic text-xl mt-6 leading-relaxed flex-1"
                    style={{ color: "#1A1A1A", fontWeight: 300 }}
                  >
                    „{t.quote}”
                  </p>
                  <div className="mt-8 pt-6" style={{ borderTop: "1px solid #E8E0D5" }}>
                    <p className="font-serif-display text-lg" style={{ color: "#1A1A1A" }}>
                      {t.name}
                    </p>
                    <p className="eyebrow mt-1" style={{ color: "#999" }}>{t.location}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* === DE CE NOI === */}
      <section className="py-28 md:py-40 relative overflow-hidden">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <div className="lg:col-span-5">
              <FadeIn>
                <p className="eyebrow mb-6">— De ce noi —</p>
                <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-tight" style={{ color: "#1A1A1A", fontWeight: 300 }}>
                  Trei <span style={{ fontStyle: "italic", color: "#B8964E" }}>principii</span> care ne definesc.
                </h2>
              </FadeIn>
              <FadeIn delay={150}>
                <div className="mt-10 overflow-hidden" style={{ aspectRatio: "4/3", border: "1px solid #E8E0D5" }}>
                  <img
                    src={INTERIOR_IMAGE}
                    alt="Interior salon Lumière"
                    className="w-full h-full object-cover"
                    style={{ filter: "saturate(0.9) brightness(1.02)" }}
                  />
                </div>
              </FadeIn>
            </div>

            <div className="lg:col-span-7 flex flex-col gap-10">
              {[
                {
                  icon: Leaf,
                  title: "Produse Premium",
                  desc: "Lucrăm cu mărci selectate cu grijă — Olaplex, Davines, Kérastase, Dior. Fiecare formulă susține rezultate vizibile și ingrediente respectuoase cu pielea.",
                },
                {
                  icon: Award,
                  title: "Specialiști Certificați",
                  desc: "Echipa noastră se formează anual la academii din Paris, Milano și Londra. Tehnica este standardul — empatia este semnătura.",
                },
                {
                  icon: Sparkles,
                  title: "Atmosferă Exclusivistă",
                  desc: "Spațiu intim, programări limitate, muzică liniștită și o ceașcă de ceai sau cafea de specialitate. Timpul tău aici este protejat.",
                },
              ].map((p, i) => (
                <FadeIn key={p.title} delay={i * 120}>
                  <div className="flex gap-6 items-start group">
                    <div
                      className="shrink-0 w-14 h-14 flex items-center justify-center transition-colors duration-500 group-hover:bg-[#B8964E]"
                      style={{ border: "1px solid #B8964E", color: "#B8964E" }}
                    >
                      <p.icon size={20} className="transition-colors duration-500 group-hover:text-white" />
                    </div>
                    <div>
                      <h3 className="font-serif-display text-2xl sm:text-3xl" style={{ color: "#1A1A1A", fontWeight: 300 }}>
                        {p.title}
                      </h3>
                      <p className="mt-3 text-sm leading-relaxed max-w-lg" style={{ color: "#555" }}>
                        {p.desc}
                      </p>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* === CTA strip === */}
      <section className="py-20 md:py-28 marble-soft" style={{ borderTop: "1px solid #E8E0D5" }}>
        <div className="mx-auto max-w-[1100px] px-6 text-center">
          <FadeIn>
            <p className="eyebrow mb-6">— Următorul pas —</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl" style={{ color: "#1A1A1A", fontWeight: 300 }}>
              Rezervă-ți momentul tău <span style={{ fontStyle: "italic", color: "#B8964E" }}>de lumină</span>.
            </h2>
            <p className="mt-6 text-base" style={{ color: "#555" }}>
              {CONTACT.address} · {CONTACT.phone}
            </p>
            <div className="mt-10">
              <Link to="/rezervari" data-testid="home-bottom-cta" className="btn-lux">
                Programează-te acum
                <ArrowUpRight size={14} />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </main>
  );
}
